// The WhatsApp chats the assistant handed to a person (Stage 4.5, the owner's decision 3): how many are waiting for someone to
// answer, for the badge beside "Messaging" on every page, and the packets themselves for the inbox. With `onFresh`, a new one
// waiting is handed to the caller (the inbox's own banner and sound, inboxAlerts.js); else with `sound` it makes the order board's
// chime (orderAlerts.js). The badge never chimes.
import { useEffect, useRef, useState } from "react";
import { getWhatsAppHandoffs } from "./handoffApi";
import { armSoundOnGesture, playChime, soundWanted } from "../ai/orderAlerts";

export const WAITING_POLL_MS = 30000;

export default function useWaitingHandoffs({ enabled = true, sound = false, pollMs = WAITING_POLL_MS, onFresh = null } = {}) {
  const [state, setState] = useState({ handoffs: [], waiting: 0, open: 0 });
  const soundRef = useRef(sound);
  const onFreshRef = useRef(onFresh);
  const pollRef = useRef(null);   // a look now, after a change made here (a hand-back)
  useEffect(() => { soundRef.current = sound; }, [sound]);
  useEffect(() => { onFreshRef.current = onFresh; }, [onFresh]);

  useEffect(() => {
    if (!enabled) { setState({ handoffs: [], waiting: 0, open: 0 }); return undefined; }
    let alive = true;
    let seen = null;   // the waiting packets' ids already known, so only a new one chimes
    const poll = async () => {
      try {
        const data = await getWhatsAppHandoffs();
        if (!alive) return;
        const waitingIds = (data?.handoffs ?? []).filter((h) => !h.answeredAt).map((h) => h.id);
        const fresh = seen ? waitingIds.filter((id) => !seen.has(id)) : [];
        seen = new Set(waitingIds);
        if (fresh.length && onFreshRef.current) onFreshRef.current((data?.handoffs ?? []).filter((h) => fresh.includes(h.id)));
        else if (fresh.length && soundRef.current && soundWanted()) playChime();
        setState({ handoffs: data?.handoffs ?? [], waiting: Number(data?.waiting ?? 0), open: Number(data?.open ?? 0) });
      } catch { /* a dropped poll changes nothing */ }
    };
    pollRef.current = poll;
    poll();
    const id = setInterval(poll, pollMs);
    const offGesture = sound ? armSoundOnGesture() : () => {};
    return () => { alive = false; pollRef.current = null; clearInterval(id); offGesture(); };
  }, [enabled, pollMs, sound]);

  return { ...state, refresh: () => pollRef.current?.() };
}
